import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SupportEmail from "../components/SupportEmail";
import '../styles/feedback-page.css';

const FeedbackPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ 
    message: '', 
    email: '' 
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const API_BASE_URL = 'https://api.omegavoid.com/feedback';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate message
      if (!formData.message.trim()) {
        throw new Error('Please enter your feedback message');
      }

      // Validate email (now required)
      if (!formData.email.trim()) {
        throw new Error('Email is required');
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        throw new Error('Please enter a valid email address');
      }

      // Get user data from localStorage
      const getUsername = () => {
        try {
          const authToken = localStorage.getItem('authToken');
          if (authToken) {
            const tokenPayload = JSON.parse(atob(authToken.split('.')[1]));
            return tokenPayload['cognito:username'] || tokenPayload.username || 'User';
          }
        } catch (e) {
          console.error('Error parsing token:', e);
        }
        return 'anonymous';
      };

      const feedbackData = {
        message: formData.message,
        email: formData.email.trim().toLowerCase(), // Normalize email
        username: getUsername(),
        sessionId: localStorage.getItem('tempUserId') || 'unknown',
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language
      };

      // Submit to API
      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken') || ''
        },
        body: JSON.stringify(feedbackData)
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 409) {
          throw new Error('You have already submitted feedback with this email address. Only one feedback per email is allowed.');
        }
        throw new Error(data.error || 'Failed to submit feedback');
      }

      // Store success in localStorage for tracking
      const events = JSON.parse(localStorage.getItem('feedbackEvents') || '[]');
      events.push({
        type: 'feedback_submitted',
        timestamp: new Date().toISOString(),
        email: formData.email
      });
      localStorage.setItem('feedbackEvents', JSON.stringify(events));
      localStorage.setItem('lastFeedbackEmail', formData.email.toLowerCase());

      setSubmitted(true);
      
      // Auto-redirect after success
      setTimeout(() => {
        navigate(-1);
      }, 3000);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  if (submitted) {
    return (
      <div className="feedback-container">
        <div className="feedback-section success">
          <div className="success-icon">✓</div>
          <h2>Thank You!</h2>
          <p>Your feedback has been received and helps us improve the Omega Void experience.</p>
          <p>Redirecting you back...</p>
          
          <button 
            className="feedback-back-button"
            onClick={() => navigate(-1)}
          >
            ← Go Back Now
          </button>
        </div>
        <SupportEmail />
      </div>
    );
  }

  return (
    <div className="feedback-container">
      {/* <button 
        className="feedback-back-button-top"
        onClick={() => navigate(-1)}
        aria-label="Go back"
      >
        ← Back
      </button> */}

      <h1>Share Your Feedback</h1>
      
      <div className="feedback-section general">
        <h2>We Value Your Input</h2>
        <p>
          Your feedback helps us improve the Omega Void experience. Share your thoughts, 
          report issues, or suggest new features.
        </p>
        
        <div className="feedback-form-group">
          <label htmlFor="feedback-message">Your Message *</label>
          <textarea
            id="feedback-message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            placeholder="Share your feedback, suggestions, or report issues..."
            rows={6}
            maxLength={1000}
            className="feedback-form-input"
            required
          />
          <div className="character-count">
            {formData.message.length}/1000
          </div>
        </div>

        <div className="feedback-form-group">
          <label htmlFor="feedback-email">Your email for follow-up and newsletter *</label>
          <input
            type="email"
            id="feedback-email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="your@email.com"
            className="feedback-form-input"
            required
          />
          <div className="input-hint">
            Required - Only one feedback per email address allowed
          </div>
        </div>

        {error && <div className="feedback-error">{error}</div>}

        <button 
          type="submit"
          disabled={loading || !formData.message.trim() || !formData.email.trim()} 
          className="feedback-submit-button"
          onClick={handleSubmit}
        >
          {loading ? (
            <span className="loading-spinner">⏳ Submitting...</span>
          ) : (
            'Submit Feedback'
          )}
        </button>
      </div>

      <div className="feedback-section corporate">
        <div className="verified-badge">
          <span className="verified-icon">✓</span> Secure Feedback
        </div>
        
        <h2>Privacy & Security</h2>
        <p className="corporate-message">
          Your feedback is transmitted securely and stored with privacy protection. 
          Email addresses are only used for follow-up communication if provided.
        </p>
        
        <div className="security-note">
          <p>
            <strong>Security Notice:</strong> All feedback is processed through encrypted 
            channels and stored according to our privacy policy.
          </p>
        </div>
      </div>

      <SupportEmail />
    </div>
  );
};

export default FeedbackPage;